import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  flattenLocations,
  formatCount,
  formatMoney,
  matchesStockFilters,
  pathLabel,
  reserveOwner,
  RETIRE_REASONS,
  RETIRE_REASON_LABELS,
  TRACKING_LABELS,
  type Client,
  type OpenDeployment,
  type Reserve,
  type RetireReason,
  type StockFilters,
  type StockOverview,
  type StockRow,
  type StockTracking,
} from "@iltocat/shared";
import { ApiError, api } from "../lib/api.js";
import type { Taxonomy } from "../lib/useTaxonomy.js";
import type { Vocabulary } from "../lib/useVocabulary.js";
import { calendarDay } from "../lib/stockFormat.js";
import { ClientTag, clientColors } from "../components/ClientTag.js";
import { EmptyState } from "../components/EmptyState.js";
import { FilterChip, type FilterOption } from "../components/FilterChip.js";
import { Icon, type IconName } from "../components/Icon.js";
import { Menu, MenuItem, Segmented } from "@vcnet/ui";
import { Notice } from "../components/Notice.js";
import { PageHeader } from "../components/PageHeader.js";
import { Panel } from "../components/Panel.js";
import { AttachmentLink } from "../components/StockPanel.js";
import {
  DeployPanel,
  PricePanel,
  ConvertToBulkPanel,
  ConvertToSerializedPanel,
  RetirePanel,
  ReturnPanel,
  SpotPanel,
  TransferPanel,
  knownSpots,
} from "../components/StockForms.js";
import { useToast } from "../components/Toast.js";

type Pile = "shelf" | "out" | "retired";

/**
 * The stockroom: what is on the shelf, what is out at clients, what has been
 * retired -- and the three things that move stock between them.
 *
 * **Deploy** is a selection then a panel: tick what is going, pick the client
 * once, and each line's price starts at cost plus the inventory's markup,
 * ready to be typed over (the owner's "markup, with an override per line").
 * Billing is a switch per line and on by default; whether a laptop coming
 * back from one client is billed again when it goes to the next is the
 * deployer's call, every time.
 *
 * **Return**, **Move to another client** and **Retire** work on one row from
 * its own menu (the bordered "more" button at the end of the row, one button
 * rather than a row of faint words), or on everything ticked from the bar at
 * the bottom: tick three laptops at Branch A and move them to Branch B in one
 * go, which the server records as back from A and out to B on the same day.
 * Pricing from cost is the same: a row's menu, or the bar for what is ticked,
 * never a standing button over the whole screen. So is printing: Labels on the
 * bar opens labels or cards for everything ticked, in one print run. Nothing
 * here deletes anything.
 *
 * A deployed line is at a client, shown as the client's coloured tag, with an
 * optional note of where there ("Desk 14", "Remote: J. Smith") beside it.
 *
 * **Filters** are the Items screen's chips, per pile: category, location, tag
 * and kind everywhere they mean something, plus who it is held for on the
 * shelf, the client on the Deployed pile and the reason on the Retired one.
 * Each chip offers only what is in the pile, with how many, and switching
 * pile clears them: a client picked on Deployed means nothing on the shelf.
 */
export function Stock({
  taxonomy,
  vocabulary,
  onOpen,
  onLabels,
  onReceive,
  onReserves,
}: {
  taxonomy: Taxonomy;
  vocabulary: Vocabulary;
  onOpen: (id: string) => void;
  /** Labels or cards for what is ticked, one print run. */
  onLabels: (itemIds: string[]) => void;
  onReceive: () => void;
  onReserves: () => void;
}) {
  const [overview, setOverview] = useState<StockOverview | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [markup, setMarkup] = useState(0);
  const [pile, setPile] = useState<Pile>("shelf");
  const [filter, setFilter] = useState("");
  /** The chips, picked. Cleared when the pile changes. */
  const [chips, setChips] = useState<StockFilters>({});
  /** The Deployed pile narrowed to one client. */
  const [client, setClient] = useState<string | undefined>(undefined);
  /** The shelf narrowed by who its units are held for: "none", "any", or a client id. */
  const [held, setHeld] = useState<string | undefined>(undefined);
  /** The Retired pile narrowed to one reason. */
  const [reason, setReason] = useState<RetireReason | undefined>(undefined);
  /** Shelf rows ticked, by item id. */
  const [picked, setPicked] = useState<Set<string>>(new Set());
  /** Deployed lines ticked, by deployment id. */
  const [pickedOut, setPickedOut] = useState<Set<string>>(new Set());
  const [panel, setPanel] = useState<
    | { kind: "deploy"; rows: StockRow[] }
    | { kind: "price"; rows: StockRow[] }
    | { kind: "retire"; rows: StockRow[] }
    | { kind: "to-bulk"; rows: StockRow[]; sameName: boolean }
    | { kind: "to-serialized"; row: StockRow }
    | { kind: "return"; lines: OpenDeployment[] }
    | { kind: "move"; lines: OpenDeployment[] }
    | { kind: "spot"; lines: OpenDeployment[] }
    | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [short, setShort] = useState<Reserve[]>([]);

  const load = useCallback(async () => {
    try {
      const [stock, list, settings, reserves] = await Promise.all([
        api.stock.overview(),
        api.clients.list(),
        api.stock.settings(),
        api.reserves.list(),
      ]);
      setOverview(stock);
      setClients(list.clients);
      setMarkup(settings.settings.markupPercent);
      setShort(reserves.reserves.filter((reserve) => reserve.status === "short"));
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not load the stockroom.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const done = () => {
    setPanel(null);
    setPicked(new Set());
    setPickedOut(new Set());
    void load();
  };

  const needle = filter.trim().toLowerCase();
  const matches = (...values: string[]) =>
    !needle || values.some((value) => value.toLowerCase().includes(needle));

  // Plain filters on every render: a stockroom's lists are hundreds of rows at
  // most, and the filter is typed into one character at a time.
  // The shelf is what is physically on it. A bulk line at zero used to stay
  // listed here, as where the next delivery goes, with its tick box disabled
  // -- which read as boxes that randomly refused to tick. Receiving still
  // offers every bulk line, whatever it has on hand.
  const onShelf = (overview?.items ?? []).filter((row) => row.state === "in_stock" && row.onHand > 0);
  const facets = overview?.facets ?? {};
  const parentOf = (id: string) => taxonomy.byId.get(id)?.parentId ?? null;
  const chipsPass = (itemId: string) => matchesStockFilters(facets[itemId], chips, parentOf);
  const heldPasses = (row: StockRow) =>
    held === undefined ||
    (held === "none"
      ? row.holds.length === 0
      : held === "any"
        ? row.holds.length > 0
        : row.holds.some((hold) => hold.clientId === held));
  const shelf = onShelf.filter(
    (row) => matches(row.title, row.serial) && chipsPass(row.itemId) && heldPasses(row),
  );
  const out = (overview?.open ?? []).filter(
    (line) =>
      (!client || line.clientId === client) &&
      matches(line.title, line.serial, line.clientName, line.spot) &&
      chipsPass(line.itemId),
  );
  const retired = (overview?.retired ?? []).filter(
    (row) => matches(row.title, row.serial) && (!reason || row.reason === reason) && chipsPass(row.itemId),
  );
  /** Something narrows the pile beyond what it is, so an empty one says "nothing matches". */
  const narrowed =
    Boolean(needle) ||
    Object.values(chips).some((value) => value !== undefined) ||
    (pile === "shelf" && held !== undefined) ||
    (pile === "out" && client !== undefined) ||
    (pile === "retired" && reason !== undefined);
  const clearFilters = () => {
    setChips({});
    setClient(undefined);
    setHeld(undefined);
    setReason(undefined);
    setPicked(new Set());
    setPickedOut(new Set());
  };
  const setChip = (change: StockFilters) => {
    setChips((now) => ({ ...now, ...change }));
    setPicked(new Set());
    setPickedOut(new Set());
  };

  /** The item ids in the pile on screen, before the chips: what each chip counts. */
  const pileItemIds =
    pile === "shelf"
      ? onShelf.map((row) => row.itemId)
      : pile === "out"
        ? (overview?.open ?? []).map((line) => line.itemId)
        : (overview?.retired ?? []).map((row) => row.itemId);
  const locationLabels = useMemo(
    () => new Map(flattenLocations(taxonomy.tree).map((node) => [node.id, pathLabel(node.path)])),
    [taxonomy.tree],
  );
  const facetOptions = chipOptions(pileItemIds, facets, {
    categories: new Map(taxonomy.categories.map((one) => [one.id, one.name])),
    locations: locationLabels,
    tags: new Map(vocabulary.tags.map((tag) => [tag.id, tag.name])),
    parentOf,
  });

  const flip = (set: (update: (now: Set<string>) => Set<string>) => void) => (id: string) =>
    set((now) => {
      const next = new Set(now);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggle = flip(setPicked);
  const toggleOut = flip(setPickedOut);
  /** Tick or untick every row the filter left showing; ticks on hidden rows are dropped either way. */
  const pickShelf = (pick: boolean) => setPicked(new Set(pick ? shelf.map((row) => row.itemId) : []));
  const pickOut = (pick: boolean) => setPickedOut(new Set(pick ? out.map((line) => line.id) : []));

  const totals = overview?.totals;
  const stats: { icon: IconName; label: string; value: string }[] = totals
    ? [
        { icon: "box", label: "Units on the shelf", value: formatCount(totals.onHand) },
        { icon: "external", label: "Units out at clients", value: formatCount(totals.deployed) },
        {
          icon: "lock",
          label: "Shelf value at cost",
          value: formatMoney(totals.onHandCostCents),
        },
        { icon: "clipboard", label: "Lines waiting to bill", value: formatCount(totals.unbilledLines) },
      ]
    : [];

  const pickedRows = (overview?.items ?? []).filter((row) => picked.has(row.itemId));
  const colors = clientColors(clients);
  const spots = knownSpots(overview?.open ?? []);
  /** A row's menu opens that row's own panel, with nothing else ticked into it. */
  const only = (next: NonNullable<typeof panel>) => {
    setPicked(new Set());
    setPickedOut(new Set());
    setPanel(next);
  };
  const pickedLines = (overview?.open ?? []).filter((line) => pickedOut.has(line.id));
  const rowOf = (itemId: string) => overview?.items.find((row) => row.itemId === itemId);
  // Only a serialized item can be retired from a client site; bulk units come back first.
  const retirableOut = pickedLines.every((line) => line.tracking === "serialized");
  const allOutPicked = out.length > 0 && out.every((line) => pickedOut.has(line.id));
  /** The clients something is out at, for the Deployed pile's chip. Nobody filters to an empty list. */
  const outAt = clients
    .map((one) => ({
      value: one.id,
      label: one.name,
      count: (overview?.open ?? []).filter((line) => line.clientId === one.id).length,
    }))
    .filter((one) => one.count > 0 || one.value === client);
  /** Who shelf units are held for, by client, plus "not held" and "held for anybody". */
  const heldBy = new Map<string, { name: string; count: number }>();
  for (const row of onShelf) {
    for (const clientId of new Set(row.holds.map((hold) => hold.clientId))) {
      const name = row.holds.find((hold) => hold.clientId === clientId)?.clientName ?? "";
      heldBy.set(clientId, { name, count: (heldBy.get(clientId)?.count ?? 0) + 1 });
    }
  }
  const heldOptions: FilterOption<string>[] =
    heldBy.size === 0
      ? []
      : [
          { value: "none", label: "Not held", count: onShelf.filter((row) => row.holds.length === 0).length },
          {
            value: "any",
            label: "Held for a client",
            count: onShelf.filter((row) => row.holds.length > 0).length,
          },
          ...[...heldBy]
            .sort(([, a], [, b]) => a.name.localeCompare(b.name))
            .map(([value, one]) => ({ value, label: `Held for ${one.name}`, count: one.count })),
        ];
  const reasonOptions: FilterOption<RetireReason>[] = RETIRE_REASONS.map((value) => ({
    value,
    label: RETIRE_REASON_LABELS[value],
    count: (overview?.retired ?? []).filter((row) => row.reason === value).length,
  })).filter((option) => option.count > 0 || option.value === reason);
  const allShelfPicked = shelf.length > 0 && shelf.every((row) => picked.has(row.itemId));
  const someShelfPicked = !allShelfPicked && shelf.some((row) => picked.has(row.itemId));
  const someOutPicked = !allOutPicked && out.some((line) => pickedOut.has(line.id));

  return (
    <>
      <PageHeader
        title="Stock"
        actions={
          <button type="button" className="button" onClick={onReceive}>
            <Icon name="download" size={18} />
            Receive stock
          </button>
        }
      />
      {error && <Notice tone="error">{error}</Notice>}
      {short.length > 0 && (
        <div className="reserve-banner" role="status">
          <Icon name="alert" size={18} />
          <span className="reserve-banner-text">
            <strong>
              {short.length === 1 ? "1 reserve is short" : `${short.length} reserves are short`}
            </strong>
            <span className="muted">
              {" "}
              · {short.slice(0, 3).map(reserveOwner).join(", ")}
              {short.length > 3 ? ` and ${short.length - 3} more` : ""}
            </span>
          </span>
          <button type="button" className="button button-small" onClick={onReserves}>
            See reserves
          </button>
        </div>
      )}

      <ul className="stats" aria-busy={overview === null}>
        {overview === null
          ? Array.from({ length: 4 }, (_, index) => <li key={index} className="stat skeleton" />)
          : stats.map((stat) => (
              <li key={stat.label} className="stat">
                <span className="stat-icon">
                  <Icon name={stat.icon} size={18} />
                </span>
                <span className="stat-value">{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
              </li>
            ))}
      </ul>

      {panel?.kind === "deploy" && (
        <DeployPanel
          rows={panel.rows}
          clients={clients}
          markup={markup}
          spots={spots}
          onClients={setClients}
          onCancel={() => setPanel(null)}
          onDone={done}
        />
      )}
      {panel?.kind === "price" && (
        <PricePanel rows={panel.rows} markup={markup} onCancel={() => setPanel(null)} onDone={done} />
      )}
      {panel?.kind === "to-bulk" && (
        <ConvertToBulkPanel
          rows={panel.rows}
          sameName={panel.sameName}
          onCancel={() => setPanel(null)}
          onDone={done}
        />
      )}
      {panel?.kind === "to-serialized" && (
        <ConvertToSerializedPanel row={panel.row} onCancel={() => setPanel(null)} onDone={done} />
      )}
      {panel?.kind === "retire" && (
        <RetirePanel rows={panel.rows} onCancel={() => setPanel(null)} onDone={done} />
      )}
      {panel?.kind === "return" && (
        <ReturnPanel lines={panel.lines} onCancel={() => setPanel(null)} onDone={done} />
      )}
      {panel?.kind === "move" && (
        <TransferPanel
          lines={panel.lines}
          clients={clients}
          spots={spots}
          onClients={setClients}
          onCancel={() => setPanel(null)}
          onDone={done}
        />
      )}
      {panel?.kind === "spot" && (
        <SpotPanel lines={panel.lines} spots={spots} onCancel={() => setPanel(null)} onDone={done} />
      )}

      <div className="toolbar">
        <Segmented<Pile>
          label="Which stock"
          value={pile}
          onChange={(next) => {
            setPile(next);
            clearFilters();
          }}
          options={[
            { value: "shelf", label: `On the shelf${overview ? ` (${onShelf.length})` : ""}` },
            { value: "out", label: `Deployed${overview ? ` (${overview.open.length})` : ""}` },
            { value: "retired", label: `Retired${overview ? ` (${overview.retired.length})` : ""}` },
          ]}
        />
        <label className="searchbox stock-filter">
          <Icon name="search" size={16} />
          <input
            aria-label="Filter by name, serial or client"
            placeholder="Filter by name, serial or client"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          />
        </label>
      </div>
      {overview !== null && (
        <div className="filter-bar" role="group" aria-label="Filters">
          {pile === "shelf" && heldOptions.length > 0 && (
            <FilterChip
              label="Held"
              value={held}
              anyLabel="Held or not"
              options={heldOptions}
              onChange={(next) => {
                setHeld(next);
                setPicked(new Set());
              }}
            />
          )}
          {pile === "out" && (outAt.length > 1 || client !== undefined) && (
            <FilterChip
              label="Client"
              value={client}
              anyLabel="Every client"
              options={outAt}
              onChange={(next) => {
                setClient(next);
                setPickedOut(new Set());
              }}
            />
          )}
          {pile === "retired" && reasonOptions.length > 1 && (
            <FilterChip<RetireReason>
              label="Why"
              value={reason}
              anyLabel="Any reason"
              options={reasonOptions}
              onChange={setReason}
            />
          )}
          {facetOptions.categories.length > 0 && (
            <FilterChip
              label="Category"
              value={chips.category}
              options={facetOptions.categories}
              onChange={(category) => setChip({ category })}
            />
          )}
          {pile !== "out" && facetOptions.locations.length > 0 && (
            <FilterChip
              label="Location"
              value={chips.location}
              anyLabel="Anywhere"
              options={facetOptions.locations}
              onChange={(location) => setChip({ location })}
            />
          )}
          {facetOptions.tags.length > 0 && (
            <FilterChip
              label="Tag"
              value={chips.tag}
              options={facetOptions.tags}
              onChange={(tag) => setChip({ tag })}
            />
          )}
          {facetOptions.kinds.length > 1 && (
            <FilterChip<StockTracking>
              label="Kind"
              value={chips.kind}
              anyLabel="Serialized or bulk"
              options={facetOptions.kinds}
              onChange={(kind) => setChip({ kind })}
            />
          )}
          {narrowed && (
            <button type="button" className="button button-quiet button-small" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>
      )}

      {overview === null ? (
        <div className="skeleton-panel" />
      ) : pile === "shelf" ? (
        <Panel>
          {shelf.length === 0 ? (
            <EmptyState
              icon="box"
              title={narrowed ? "Nothing on the shelf matches that." : "Nothing on the shelf yet."}
              action={
                narrowed ? (
                  <button type="button" className="button button-ghost" onClick={clearFilters}>
                    Clear filters
                  </button>
                ) : (
                  <button type="button" className="button" onClick={onReceive}>
                    Receive stock
                  </button>
                )
              }
            >
              {narrowed ? undefined : "Stock comes in against a vendor invoice."}
            </EmptyState>
          ) : (
            <>
              <PickAll
                narrow
                all={allShelfPicked}
                some={someShelfPicked}
                count={shelf.length}
                onChange={pickShelf}
              />
              <table className="table">
                <thead>
                  <tr>
                    <th>
                      <PickAll
                        all={allShelfPicked}
                        some={someShelfPicked}
                        count={shelf.length}
                        onChange={pickShelf}
                      />
                    </th>
                    <th>Item</th>
                    <th>Serial</th>
                    <th>Kind</th>
                    <th>On shelf</th>
                    <th>Unit cost</th>
                    <th>Price</th>
                    <th>
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shelf.map((row) => {
                    return (
                      <tr key={row.itemId}>
                        <td data-label="Pick">
                          <input
                            type="checkbox"
                            className="check"
                            aria-label={`Pick ${row.title || "untitled item"}`}
                            checked={picked.has(row.itemId)}
                            onChange={() => toggle(row.itemId)}
                          />
                        </td>
                        <td data-label="Item">
                          <span className="stock-item">
                            <button type="button" className="link" onClick={() => onOpen(row.itemId)}>
                              {row.title || "Untitled item"}
                            </button>
                            <HeldBadge row={row} />
                          </span>
                        </td>
                        <td data-label="Serial" className="mono">
                          {row.serial || <span className="faint">none</span>}
                        </td>
                        <td data-label="Kind">{TRACKING_LABELS[row.tracking]}</td>
                        <td data-label="On shelf">
                          {/* One child, so the phone's label-and-value row does not split it. */}
                          <span>
                            {formatCount(row.onHand)}
                            {row.deployed > 0 && (
                              <span className="muted"> · {formatCount(row.deployed)} out</span>
                            )}
                          </span>
                        </td>
                        <td data-label="Unit cost">
                          {row.unitCostCents === null ? "" : formatMoney(row.unitCostCents)}
                        </td>
                        <td data-label="Price">
                          {row.priceCents === null ? (
                            <span className="faint">none</span>
                          ) : (
                            formatMoney(row.priceCents)
                          )}
                        </td>
                        <td className="cell-action">
                          <RowMenu name={row.title || "untitled item"}>
                            {(close) => (
                              <>
                                <MenuItem
                                  icon={<Icon name="external" />}
                                  onClick={() => {
                                    close();
                                    only({ kind: "deploy", rows: [row] });
                                  }}
                                >
                                  Deploy…
                                </MenuItem>
                                <MenuItem
                                  icon={<Icon name="tag" />}
                                  disabled={row.unitCostCents === null}
                                  onClick={() => {
                                    close();
                                    only({ kind: "price", rows: [row] });
                                  }}
                                >
                                  {row.unitCostCents === null
                                    ? "Price at cost (no cost given)"
                                    : "Price at cost…"}
                                </MenuItem>
                                <MenuItem
                                  icon={<Icon name="archive" />}
                                  onClick={() => {
                                    close();
                                    only({ kind: "retire", rows: [row] });
                                  }}
                                >
                                  Retire…
                                </MenuItem>
                                <MenuItem
                                  icon={<Icon name="box" />}
                                  onClick={() => {
                                    close();
                                    only(
                                      row.tracking === "serialized"
                                        ? { kind: "to-bulk", rows: [row], sameName: true }
                                        : { kind: "to-serialized", row },
                                    );
                                  }}
                                >
                                  {row.tracking === "serialized"
                                    ? "Convert to bulk…"
                                    : "Convert to serialized…"}
                                </MenuItem>
                                {row.tracking === "bulk" && (
                                  <MenuItem
                                    icon={<Icon name="plus" />}
                                    onClick={() => {
                                      close();
                                      only({ kind: "to-bulk", rows: [row], sameName: false });
                                    }}
                                  >
                                    Merge other stock into it…
                                  </MenuItem>
                                )}
                              </>
                            )}
                          </RowMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </Panel>
      ) : pile === "out" ? (
        <Panel>
          {out.length === 0 ? (
            <EmptyState
              icon="external"
              title={narrowed ? "Nothing deployed matches that." : "Nothing is out at a client."}
            />
          ) : (
            <>
              <PickAll narrow all={allOutPicked} some={someOutPicked} count={out.length} onChange={pickOut} />
              <table className="table">
                <thead>
                  <tr>
                    <th>
                      <PickAll
                        all={allOutPicked}
                        some={someOutPicked}
                        count={out.length}
                        onChange={pickOut}
                      />
                    </th>
                    <th>Item</th>
                    <th>Serial</th>
                    <th>Client</th>
                    <th>Out</th>
                    <th>Price</th>
                    <th>Went out</th>
                    <th>Billing</th>
                    <th>
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {out.map((line) => {
                    const row = rowOf(line.itemId);
                    return (
                      <tr key={line.id}>
                        <td data-label="Pick">
                          <input
                            type="checkbox"
                            className="check"
                            aria-label={`Pick ${line.title || "untitled item"} at ${line.clientName}`}
                            checked={pickedOut.has(line.id)}
                            onChange={() => toggleOut(line.id)}
                          />
                        </td>
                        <td data-label="Item">
                          <button type="button" className="link" onClick={() => onOpen(line.itemId)}>
                            {line.title || "Untitled item"}
                          </button>
                        </td>
                        <td data-label="Serial" className="mono">
                          {line.serial || <span className="faint">none</span>}
                        </td>
                        <td data-label="Client">
                          <ClientTag
                            name={line.clientName}
                            color={colors.get(line.clientId)}
                            spot={line.spot}
                          />
                        </td>
                        <td data-label="Out">{line.quantity - line.returned}</td>
                        <td data-label="Price">{formatMoney(line.unitPriceCents)}</td>
                        <td data-label="Went out">{calendarDay(line.deployedOn)}</td>
                        <td data-label="Billing">
                          <BillingPill billable={line.billable} billedAt={line.billedAt} />
                        </td>
                        <td className="cell-action">
                          <RowMenu name={`${line.title || "untitled item"} at ${line.clientName}`}>
                            {(close) => (
                              <>
                                <MenuItem
                                  icon={<Icon name="pin" />}
                                  onClick={() => {
                                    close();
                                    only({ kind: "spot", lines: [line] });
                                  }}
                                >
                                  Where at the client…
                                </MenuItem>
                                <MenuItem
                                  icon={<Icon name="external" />}
                                  onClick={() => {
                                    close();
                                    only({ kind: "move", lines: [line] });
                                  }}
                                >
                                  Move to another client…
                                </MenuItem>
                                <MenuItem
                                  icon={<Icon name="download" />}
                                  onClick={() => {
                                    close();
                                    only({ kind: "return", lines: [line] });
                                  }}
                                >
                                  Return to the shelf…
                                </MenuItem>
                                {line.tracking === "serialized" && row && (
                                  <MenuItem
                                    icon={<Icon name="archive" />}
                                    onClick={() => {
                                      close();
                                      only({ kind: "retire", rows: [row] });
                                    }}
                                  >
                                    Retire…
                                  </MenuItem>
                                )}
                              </>
                            )}
                          </RowMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </Panel>
      ) : (
        <Panel>
          {retired.length === 0 ? (
            <EmptyState
              icon="clock"
              title={narrowed ? "Nothing retired matches that." : "Nothing has been retired."}
            />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Serial</th>
                  <th>Units</th>
                  <th>Why</th>
                  <th>When</th>
                  <th>Proof</th>
                </tr>
              </thead>
              <tbody>
                {retired.map((row) => (
                  <tr key={row.id}>
                    <td data-label="Item">
                      <button type="button" className="link" onClick={() => onOpen(row.itemId)}>
                        {row.title || "Untitled item"}
                      </button>
                    </td>
                    <td data-label="Serial" className="mono">
                      {row.serial || <span className="faint">none</span>}
                    </td>
                    <td data-label="Units">{row.quantity}</td>
                    <td data-label="Why">
                      <span>
                        {RETIRE_REASON_LABELS[row.reason]}
                        {row.disposalVendor && <span className="muted"> · {row.disposalVendor}</span>}
                      </span>
                    </td>
                    <td data-label="When">{calendarDay(row.retiredOn)}</td>
                    <td data-label="Proof">
                      {row.certificate ? (
                        <AttachmentLink attachment={row.certificate} />
                      ) : row.reason === "entered_by_mistake" ? (
                        // Nothing was there to destroy.
                        <span className="faint">n/a</span>
                      ) : (
                        <CertificateUpload retirementId={row.id} onDone={() => void load()} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      )}

      {pile === "shelf" && picked.size > 0 && panel === null && (
        <div className="action-bar action-bar-float">
          <p className="action-bar-status">{picked.size} picked</p>
          <button type="button" className="button button-quiet" onClick={() => setPicked(new Set())}>
            Clear
          </button>
          <button
            type="button"
            className="button button-ghost"
            onClick={() => onLabels(pickedRows.map((row) => row.itemId))}
          >
            <Icon name="qr" size={18} />
            Labels
          </button>
          <button
            type="button"
            className="button button-ghost"
            disabled={pickedRows.every((row) => row.unitCostCents === null)}
            title={
              pickedRows.every((row) => row.unitCostCents === null)
                ? "None of these has a unit cost to price from."
                : undefined
            }
            onClick={() => setPanel({ kind: "price", rows: pickedRows })}
          >
            Price at cost…
          </button>
          <button
            type="button"
            className="button button-ghost"
            onClick={() => setPanel({ kind: "retire", rows: pickedRows })}
          >
            Retire…
          </button>
          {pickedRows.length > 1 && (
            <button
              type="button"
              className="button button-ghost"
              onClick={() => setPanel({ kind: "to-bulk", rows: pickedRows, sameName: false })}
            >
              Convert to bulk…
            </button>
          )}
          <button
            type="button"
            className="button"
            onClick={() => setPanel({ kind: "deploy", rows: pickedRows })}
          >
            <Icon name="external" size={18} />
            Deploy…
          </button>
        </div>
      )}

      {pile === "out" && pickedOut.size > 0 && panel === null && (
        <div className="action-bar action-bar-float">
          <p className="action-bar-status">{pickedOut.size} picked</p>
          <button type="button" className="button button-quiet" onClick={() => setPickedOut(new Set())}>
            Clear
          </button>
          <button
            type="button"
            className="button button-ghost"
            // A bulk line out at two clients is two ticked lines but one item, so one label.
            onClick={() => onLabels([...new Set(pickedLines.map((line) => line.itemId))])}
          >
            <Icon name="qr" size={18} />
            Labels
          </button>
          <button
            type="button"
            className="button button-ghost"
            disabled={!retirableOut}
            title={retirableOut ? undefined : "Bulk units are retired from the shelf: return them first."}
            onClick={() =>
              setPanel({
                kind: "retire",
                rows: pickedLines.flatMap((line) => rowOf(line.itemId) ?? []),
              })
            }
          >
            Retire…
          </button>
          <button
            type="button"
            className="button button-ghost"
            onClick={() => setPanel({ kind: "return", lines: pickedLines })}
          >
            Return…
          </button>
          <button
            type="button"
            className="button button-ghost"
            onClick={() => setPanel({ kind: "spot", lines: pickedLines })}
          >
            Where…
          </button>
          <button
            type="button"
            className="button"
            onClick={() => setPanel({ kind: "move", lines: pickedLines })}
          >
            <Icon name="external" size={18} />
            Move to client…
          </button>
        </div>
      )}
    </>
  );
}

/**
 * One row's actions behind one button: bordered, so it reads as a button,
 * with the "more" dots every other overflow menu in the app uses. A sheet
 * from the bottom on a phone, like every `Menu`.
 */
function RowMenu({ name, children }: { name: string; children: (close: () => void) => ReactNode }) {
  return (
    <Menu
      label={`Actions for ${name}`}
      buttonClassName="icon-button icon-button-bordered"
      button={<Icon name="more" size={20} />}
    >
      {children}
    </Menu>
  );
}

/**
 * "Tick everything listed", which is everything the filter left, not the
 * whole pile. A box in the header on a wide screen; on a phone the header is
 * hidden (each row is a card), so the same box stands above the cards with
 * words beside it. Half-ticked when some are.
 */
function PickAll({
  all,
  some,
  count,
  onChange,
  narrow = false,
}: {
  all: boolean;
  some: boolean;
  count: number;
  onChange: (pick: boolean) => void;
  narrow?: boolean;
}) {
  const box = (
    <input
      type="checkbox"
      className="check"
      aria-label={all ? "Untick everything listed" : `Tick all ${count} listed`}
      checked={all}
      ref={(node) => {
        if (node) node.indeterminate = some;
      }}
      onChange={() => onChange(!all)}
    />
  );
  if (!narrow) return box;
  return (
    <label className="pick-all">
      {box}
      <span>{all ? "Untick all" : `Tick all ${count}`}</span>
    </label>
  );
}

/**
 * Who a shelf row's units are held for: the indicator a held unit wears
 * wherever it is listed. A serialized unit names the client; a bulk line says
 * how many of its units are spoken for, and for whom.
 */
export function HeldBadge({ row }: { row: Pick<StockRow, "holds" | "tracking"> }) {
  if (row.holds.length === 0) return null;
  const held = row.holds.reduce((sum, hold) => sum + hold.quantity, 0);
  const names = row.holds.map((hold) => hold.clientName).join(", ");
  return (
    <span className="held-badge" title={`Held in reserve for ${names}`}>
      <Icon name="bookmark" size={12} />
      {row.tracking === "bulk" ? `${held} held · ${names}` : `Held · ${names}`}
    </span>
  );
}

export function BillingPill({ billable, billedAt }: { billable: boolean; billedAt: number | null }) {
  if (!billable) return <span className="pill">Not billable</span>;
  if (billedAt) return <span className="pill pill-ok">Billed</span>;
  return <span className="pill pill-warn">Waiting</span>;
}

function CertificateUpload({ retirementId, onDone }: { retirementId: string; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  return (
    <label className="button button-ghost button-small button-file">
      <Icon name="upload" size={14} />
      {busy ? "Uploading…" : "Add certificate"}
      <input
        type="file"
        className="hidden-input"
        accept="application/pdf,image/jpeg,image/png,image/webp"
        disabled={busy}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (!file) return;
          setBusy(true);
          void api.stock
            .certificate(retirementId, file)
            .then(() => {
              toast("Certificate kept.");
              onDone();
            })
            .catch((problem: unknown) =>
              toast(problem instanceof ApiError ? problem.message : "That did not upload."),
            )
            .finally(() => setBusy(false));
        }}
      />
    </label>
  );
}

/**
 * Each facet chip's choices: only what is in the pile, with how many rows
 * have it. A rows count, not units: a bulk line of 1,240 docks is one row.
 * A location counts what is filed anywhere below it, because picking it
 * shows those too; a shelf with nothing directly on it is still offered.
 */
function chipOptions(
  itemIds: string[],
  facets: StockOverview["facets"],
  names: {
    categories: Map<string, string>;
    locations: Map<string, string>;
    tags: Map<string, string>;
    parentOf: (locationId: string) => string | null;
  },
): {
  categories: FilterOption<string>[];
  locations: FilterOption<string>[];
  tags: FilterOption<string>[];
  kinds: FilterOption<StockTracking>[];
} {
  const tally = () => new Map<string, number>();
  const bump = (map: Map<string, number>, key: string) => map.set(key, (map.get(key) ?? 0) + 1);
  const categories = tally();
  const locations = tally();
  const tags = tally();
  const kinds = tally();
  for (const id of itemIds) {
    const one = facets[id];
    if (!one) continue;
    bump(categories, one.categoryId ?? "none");
    if (one.locationId === null) bump(locations, "none");
    for (let at: string | null = one.locationId, steps = 0; at !== null && steps < 64; steps += 1) {
      bump(locations, at);
      at = names.parentOf(at);
    }
    for (const tag of one.tagIds) bump(tags, tag);
    bump(kinds, one.tracking);
  }
  const options = (map: Map<string, number>, label: (key: string) => string | undefined, none: string) =>
    [...map]
      .map(([value, count]) => ({ value, count, label: value === "none" ? none : (label(value) ?? "") }))
      .filter((option) => option.label !== "")
      .sort((a, b) => (a.value === "none" ? 1 : b.value === "none" ? -1 : a.label.localeCompare(b.label)));
  // A chip whose only choice is "none" says nothing worth a tap.
  const useful = (list: FilterOption<string>[]) =>
    list.every((option) => option.value === "none") ? [] : list;
  return {
    categories: useful(options(categories, (id) => names.categories.get(id), "No category")),
    locations: useful(options(locations, (id) => names.locations.get(id), "Not filed anywhere")),
    tags: options(
      tags,
      (id) => {
        const name = names.tags.get(id);
        return name === undefined ? undefined : `#${name}`;
      },
      "",
    ),
    kinds: (["serialized", "bulk"] as const)
      .filter((kind) => kinds.has(kind))
      .map((kind) => ({ value: kind, label: TRACKING_LABELS[kind], count: kinds.get(kind) ?? 0 })),
  };
}
